import { computed, toRef } from 'vue';
import { useAnimatedCount } from '../../composables/useAnimatedCount.js';

// Every opt-in below resolves to "nothing" when it is off — no prop, no listener, no class, the
// number's own text — so a card that passes none renders exactly what it did before them.
const NONE = Object.freeze({});

export default (props, { emit }) => {
  // First change snaps, later changes animate (variant A)
  const { displayCount } = useAnimatedCount(toRef(props, 'count'));

  // `formatValue` (opt-in): the displayed number — mid-animation too — through the caller's
  // formatter; none → the number itself, as before.
  const displayValue = computed(() => (
    props.formatValue ? props.formatValue(displayCount.value) : displayCount.value
  ));

  // `valueColor` (opt-in): an inline colour on the number.
  const valueAttrs = computed(() => (
    props.valueColor ? { style: { color: props.valueColor } } : NONE
  ));

  // `selectable` / `active` (opt-in): standalone asset-management's clickable KPI tile. The
  // listener is bound ONLY while selectable: Vuetify turns a v-card with a click listener into a
  // clickable card (`v-card--link`, ripple, hover overlay), which a plain card must not become.
  const select = () => {
    if (props.selectable) emit('select');
  };
  const onKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      select();
    }
  };
  const selectableAttrs = () => ({
    class: ['rdrive-total-card--selectable', { 'rdrive-total-card--active': props.active }],
    role: 'button',
    tabindex: 0,
    'aria-pressed': props.active ? 'true' : 'false',
    onClick: select,
    onKeydown,
  });
  // `dense` (opt-in, Daniel 2026-10-16): standalone asset-management's compact KpiCard look —
  // rounded corners plus the namespaced class style.css sizes down (a small label that wraps,
  // so a long title and its info icon stay visible at 4-up; a text-h4-sized number).
  const cardAttrs = computed(() => {
    if (!props.selectable && !props.dense) return NONE;
    const attrs = props.selectable ? selectableAttrs() : { class: [] };
    if (props.dense) {
      attrs.class = [...attrs.class, 'rdrive-total-card--dense'];
      attrs.rounded = 'lg';
    }
    return attrs;
  });

  return {
    displayValue,
    valueAttrs,
    cardAttrs,
  };
};
